import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "../../lib/utils"
import {
  PopoverPortalContainerContext,
} from "./popover-portal-container"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Fixed/Flex
      "fixed inset-0 z-[500] " +
      // Data state
      "data-[state=open]:animate-in data-[state=closed]:animate-out " +
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 " +
      // Other
      "bg-black/80",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const dialogWidthVariants = {
  xs: 'sm:max-w-md',
  sm: 'sm:max-w-xl',
  md: 'sm:max-w-4xl',
  lg: 'sm:max-w-6xl',
  none: '',
}

const DIALOG_BACK_CLOSE_SENTINEL = '__duckDialogBackClose'

// A sentinel pop (history.back) that has been requested but whose popstate has
// not landed yet. Shared across dialogs: a dialog that opens while another one
// is closing must wait for this before pushing its own sentinel.
//
// Why: `history.back()` resolves its target at call time, but applies it
// asynchronously. If dialog B pushes its sentinel in that window, the traversal
// still lands on the entry *below* A's sentinel — past B's — and B's popstate
// handler mistakes that for a Back press and closes B. In production this
// silently cancelled the weapon picker that opens right after the time-limit
// confirmation, so timed assignments could never be started.
let pendingSentinelPop: Promise<void> | null = null

const popSentinel = () => {
  pendingSentinelPop = new Promise<void>((resolve) => {
    const done = () => {
      window.removeEventListener('popstate', done)
      clearTimeout(fallback)
      pendingSentinelPop = null
      resolve()
    }
    // Safety net so a swallowed popstate can't block later dialogs forever.
    const fallback = setTimeout(done, 500)
    window.addEventListener('popstate', done)
    window.history.back()
  })
}

// Mirrors a sentinel history entry to the dialog's `data-state`:
//   closed → open   : push sentinel
//   open   → closed : pop sentinel (so onClose's next nav lands on a clean stack)
// This keeps push/pop in sync even when Radix reuses the same Content across a
// close-then-reopen-during-animation cycle, which a mount-only push would miss.
//
// Initial push is deferred to a microtask so StrictMode's mount→cleanup→mount
// in dev doesn't leave a ghost sentinel.
const DialogBackButtonCloseEffect = ({
  closeRef,
  contentRef,
}: {
  closeRef: React.RefObject<HTMLButtonElement | null>
  contentRef: React.RefObject<HTMLElement | null>
}) => {
  React.useEffect(() => {
    const el = contentRef.current
    if (!el) return

    let cancelled = false
    let pushed = false
    let poppedByBack = false

    const push = () => {
      if (cancelled || pushed) return
      const doPush = () => {
        // Also bail if we closed again while deferred, else we'd push a
        // sentinel for a dialog that is no longer open.
        if (cancelled || pushed || el.getAttribute('data-state') !== 'open') return
        window.history.pushState({ [DIALOG_BACK_CLOSE_SENTINEL]: true }, '')
        pushed = true
        poppedByBack = false
      }
      // Let a closing dialog's pop land first so our entry ends up on top of a
      // clean stack instead of being skipped over by its traversal.
      if (pendingSentinelPop) void pendingSentinelPop.then(doPush)
      else doPush()
    }

    const pop = () => {
      if (!pushed) return
      pushed = false
      if (poppedByBack) return
      if (window.history.state?.[DIALOG_BACK_CLOSE_SENTINEL]) {
        popSentinel()
      }
    }

    const handlePopState = () => {
      // Only a pop of OUR sentinel is a Back press. Before we've pushed, any
      // popstate belongs to someone else (e.g. a sibling dialog closing).
      if (!pushed) return
      // Ignore pops that don't land us outside our sentinel — e.g. router
      // normalization or a nav that replaced the sentinel.
      if (window.history.state?.[DIALOG_BACK_CLOSE_SENTINEL]) return
      poppedByBack = true
      pushed = false
      closeRef.current?.click()
    }

    const initialTimer = setTimeout(() => {
      if (cancelled) return
      if (el.getAttribute('data-state') === 'open') push()
    }, 0)

    const observer = new MutationObserver(() => {
      const state = el.getAttribute('data-state')
      if (state === 'open') push()
      else if (state === 'closed') pop()
    })
    observer.observe(el, { attributes: true, attributeFilter: ['data-state'] })

    window.addEventListener('popstate', handlePopState)

    return () => {
      cancelled = true
      clearTimeout(initialTimer)
      observer.disconnect()
      window.removeEventListener('popstate', handlePopState)
      pop()
    }
  }, [closeRef, contentRef])

  return null
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    width?: 'xs' | 'sm' | 'md' | 'lg' | 'none'
    noMinHeight?: boolean
    overlayClassName?: string
    showCloseButton?: boolean
    noCloseButton?: boolean
    disableBackButtonClose?: boolean
  }
>(({ className, children, 'aria-describedby': ariaDescribedBy, width = 'md', noMinHeight = false, overlayClassName, showCloseButton = true, noCloseButton = false, disableBackButtonClose = false, ...props }, ref) => {
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null)
  const contentElRef = React.useRef<HTMLDivElement | null>(null)
  const contentRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      contentElRef.current = el
      if (typeof ref === 'function') ref(el)
      else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = el
      setPortalContainer(el)
    },
    [ref]
  )

  const backCloseRef = React.useRef<HTMLButtonElement>(null)

  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={contentRef}
        className={cn(
          // Mobile: Fixed/Flex — overflow-visible so tooltips/popovers aren't clipped (!important in case Radix or RemoveScroll set overflow)
          "fixed inset-0 z-[500] flex flex-col justify-center sm:justify-normal !overflow-visible",
          // Mobile: Width rules
          "w-full",
          // Mobile: Height rules
          "h-full",
          // Mobile: Data state
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          // Mobile: Other
          "bg-card p-6 shadow-lg duration-200",
          // sm: Fixed/Flex
          "sm:inset-auto sm:left-[50%] sm:top-[50%] sm:right-auto sm:bottom-auto",
          "sm:translate-x-[-50%] sm:translate-y-[-50%]",
          // sm: Width rules
          dialogWidthVariants[width],
          // sm: Height rules
          "sm:h-auto" + (noMinHeight ? "" : " sm:min-h-[200px]"),
          "sm:max-h-[90vh]",
          // sm: Data state
          "sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%]",
          "sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]",
          // sm: Other
          "sm:rounded-lg sm:border sm:border-muted",
          "outline-none focus:outline-none",
          className
        )}
        aria-describedby={ariaDescribedBy ?? undefined}
        {...props}
      >
        <PopoverPortalContainerContext.Provider value={portalContainer}>
          <div className="flex min-h-0 sm:flex-1 flex-col overflow-y-auto custom-scrollbar px-2 -mx-2">
            {children}
          </div>
          {showCloseButton && !noCloseButton && (
            <DialogPrimitive.Close className="absolute right-4 rtl:left-4 rtl:right-auto top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
          {!disableBackButtonClose && (
            <>
              <DialogBackButtonCloseEffect closeRef={backCloseRef} contentRef={contentElRef} />
              <DialogPrimitive.Close
                ref={backCloseRef}
                aria-hidden
                tabIndex={-1}
                style={{ display: 'none' }}
              />
            </>
          )}
        </PopoverPortalContainerContext.Provider>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Mobile: Fixed/Flex
      "flex flex-col flex-shrink-0 " +
      // Mobile: Other
      "space-y-1.5 text-center mb-4 " +
      // sm: Other
      "sm:text-start",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Mobile: Fixed/Flex
      "flex flex-col-reverse flex-shrink-0 " +
      // Mobile: Other
      "mt-auto " +
      // sm: Fixed/Flex
      "sm:flex-row sm:justify-end " +
      // sm: Other
      "sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight text-foreground",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
